import type { AgentIconType } from '@atlassian/do-not-use-agent-brand/agent-icon-type';

import { expValEquals } from '@confluence/feature-experiments';
import { isMcpAgentJobTitle } from '@confluence/profile/entry-points/isMcpAgentJobTitle.ts';

import { getAgentBrand } from './getAgentBrand.ts';
import { toUsableIdentityAccountId } from './toUsableIdentityAccountId.ts';

const FALLBACK_KEY_PREFIX = '__fallback__:';
export const FALLBACK_KEY_ROVO: '__fallback__:rovo_chat' = `${FALLBACK_KEY_PREFIX}rovo_chat`;
export const FALLBACK_KEY_UNKNOWN: '__fallback__:unknown' = `${FALLBACK_KEY_PREFIX}unknown`;
const ROVO_FALLBACK_DISPLAY_NAME = 'Rovo';
export type ClickableAgentIdentity = {
	accountId?: string | null;
	agentIcon?: AgentIconType;
	groupKey?: string;
	displayName?: string;
	jobTitle?: string | null;
};

export const isClickableAgentIdentity = ({
	accountId,
	agentIcon,
	groupKey,
	displayName,
	jobTitle,
}: ClickableAgentIdentity): boolean => {
	const isAriAccountId = Boolean(accountId?.startsWith('ari:'));
	if (!isAriAccountId && !toUsableIdentityAccountId(accountId)) {
		return false;
	}
	// The unknown bucket aggregates several unidentified agents, so it never resolves to a single
	// identity and stays unopenable.
	if (groupKey === FALLBACK_KEY_UNKNOWN) {
		return false;
	}
	// Only AGG's MCP markers confirm the external card before a click. An invoking person is
	// optional; unmarked identities retain their prior behavior.
	if (
		isMcpAgentJobTitle(jobTitle) &&
		expValEquals('cc_external_agent_profile_card', 'isEnabled', true)
	) {
		return true;
	}
	if (agentIcon || groupKey === FALLBACK_KEY_ROVO) {
		return false;
	}
	if (expValEquals('cc_identity_branded_agent_attribution', 'isEnabled', true)) {
		return true;
	}
	if (displayName === ROVO_FALLBACK_DISPLAY_NAME) {
		return false;
	}
	const brand = displayName ? getAgentBrand(displayName) : undefined;
	const isBrandedAgent = Boolean(agentIcon) || Boolean(brand && brand.agentIcon !== 'rovo');
	if (isBrandedAgent) {
		return false;
	}
	return true;
};
